export type WebPurchaseKind =
  | 'event_package'
  | 'event_upgrade'
  | 'host_video_addon'
  | 'guest_video_pass'
  | 'account_storage';

export type WebPurchaseCatalogItem = {
  key: string;
  kind: WebPurchaseKind;
  offeringId: string;
  packageId: string;
  productId: string;
  displayName: string;
  fallbackPriceLabel: string;
  description: string;
  packageKey?: string;
  guestLimit?: number;
  photoLimit?: number;
  hostVideoLimit?: number;
  videoQuantityAdded?: number;
  guestVideoLimit?: number;
  storageRangeLabel?: string;
  sortOrder: number;
  popular?: boolean;
};

export const WEB_REVENUECAT_OFFERINGS = {
  EVENT_PACKAGES: 'event_packages',
  EVENT_UPGRADES: 'event_upgrades',
  HOST_VIDEO_ADDONS: 'host_video_addons',
  GUEST_VIDEO_PASSES: 'guest_video_passes',
  ACCOUNT_STORAGE: 'account_storage',
} as const;

export const WEB_EVENT_PACKAGES: WebPurchaseCatalogItem[] = [
  {
    key: 'small_event',
    kind: 'event_package',
    offeringId: WEB_REVENUECAT_OFFERINGS.EVENT_PACKAGES,
    packageId: 'event_small',
    productId: 'gathrd_event_small_1299',
    packageKey: 'small_event',
    displayName: 'Small Event',
    fallbackPriceLabel: '$12.99',
    description: '50 guests · 250 photos · 5 host videos',
    guestLimit: 50,
    photoLimit: 250,
    hostVideoLimit: 5,
    sortOrder: 10,
  },
  {
    key: 'standard_event',
    kind: 'event_package',
    offeringId: WEB_REVENUECAT_OFFERINGS.EVENT_PACKAGES,
    packageId: 'event_standard',
    productId: 'gathrd_event_standard_2499',
    packageKey: 'standard_event',
    displayName: 'Standard Event',
    fallbackPriceLabel: '$24.99',
    description: '100 guests · 1,000 photos · 15 host videos',
    guestLimit: 100,
    photoLimit: 1000,
    hostVideoLimit: 15,
    sortOrder: 20,
    popular: true,
  },
  {
    key: 'large_event',
    kind: 'event_package',
    offeringId: WEB_REVENUECAT_OFFERINGS.EVENT_PACKAGES,
    packageId: 'event_large',
    productId: 'gathrd_event_large_4999',
    packageKey: 'large_event',
    displayName: 'Large Event',
    fallbackPriceLabel: '$49.99',
    description: '400 guests · 3,000 photos · 40 host videos',
    guestLimit: 400,
    photoLimit: 3000,
    hostVideoLimit: 40,
    sortOrder: 30,
  },
  {
    key: 'premier_event',
    kind: 'event_package',
    offeringId: WEB_REVENUECAT_OFFERINGS.EVENT_PACKAGES,
    packageId: 'event_premier',
    productId: 'gathrd_event_premier_8999',
    packageKey: 'premier_event',
    displayName: 'Premier Event',
    fallbackPriceLabel: '$89.99',
    description: '750 guests · 7,500 photos · 100 host videos',
    guestLimit: 750,
    photoLimit: 7500,
    hostVideoLimit: 100,
    sortOrder: 40,
  },
];

export const WEB_EVENT_UPGRADES: WebPurchaseCatalogItem[] = WEB_EVENT_PACKAGES.map((item) => ({
  ...item,
  kind: 'event_upgrade' as const,
  offeringId: WEB_REVENUECAT_OFFERINGS.EVENT_UPGRADES,
}));

export const WEB_HOST_VIDEO_ADDONS: WebPurchaseCatalogItem[] = [
  {
    key: 'host_video_10',
    kind: 'host_video_addon',
    offeringId: WEB_REVENUECAT_OFFERINGS.HOST_VIDEO_ADDONS,
    packageId: 'host_video_10',
    productId: 'gathrd_host_video_10_999',
    displayName: 'Host Video Pack 10',
    fallbackPriceLabel: '$9.99',
    description: 'Add 10 more host-video uploads to this event.',
    videoQuantityAdded: 10,
    sortOrder: 10,
  },
  {
    key: 'host_video_25',
    kind: 'host_video_addon',
    offeringId: WEB_REVENUECAT_OFFERINGS.HOST_VIDEO_ADDONS,
    packageId: 'host_video_25',
    productId: 'gathrd_host_video_25_1999',
    displayName: 'Host Video Pack 25',
    fallbackPriceLabel: '$19.99',
    description: 'Add 25 more host-video uploads to this event.',
    videoQuantityAdded: 25,
    sortOrder: 20,
  },
  {
    key: 'host_video_50',
    kind: 'host_video_addon',
    offeringId: WEB_REVENUECAT_OFFERINGS.HOST_VIDEO_ADDONS,
    packageId: 'host_video_50',
    productId: 'gathrd_host_video_50_3499',
    displayName: 'Host Video Pack 50',
    fallbackPriceLabel: '$34.99',
    description: 'Add 50 more host-video uploads to this event.',
    videoQuantityAdded: 50,
    sortOrder: 30,
  },
];

export const WEB_GUEST_VIDEO_PASSES: WebPurchaseCatalogItem[] = [
  {
    key: 'guest_video_3',
    kind: 'guest_video_pass',
    offeringId: WEB_REVENUECAT_OFFERINGS.GUEST_VIDEO_PASSES,
    packageId: 'guest_video_3',
    productId: 'gathrd_guest_video_3_299',
    displayName: 'Guest Video Pass — 3',
    fallbackPriceLabel: '$2.99',
    description: 'Upload up to 3 videos to this event as a guest.',
    guestVideoLimit: 3,
    sortOrder: 10,
  },
  {
    key: 'guest_video_10',
    kind: 'guest_video_pass',
    offeringId: WEB_REVENUECAT_OFFERINGS.GUEST_VIDEO_PASSES,
    packageId: 'guest_video_10',
    productId: 'gathrd_guest_video_10_599',
    displayName: 'Guest Video Pass — 10',
    fallbackPriceLabel: '$5.99',
    description: 'Upload up to 10 videos to this event as a guest.',
    guestVideoLimit: 10,
    sortOrder: 20,
  },
  {
    key: 'guest_video_25',
    kind: 'guest_video_pass',
    offeringId: WEB_REVENUECAT_OFFERINGS.GUEST_VIDEO_PASSES,
    packageId: 'guest_video_25',
    productId: 'gathrd_guest_video_25_999',
    displayName: 'Guest Video Pass — 25',
    fallbackPriceLabel: '$9.99',
    description: 'Upload up to 25 videos to this event as a guest.',
    guestVideoLimit: 25,
    sortOrder: 30,
  },
];

export const WEB_ACCOUNT_STORAGE: WebPurchaseCatalogItem[] = [
  {
    key: 'storage_starter',
    kind: 'account_storage',
    offeringId: WEB_REVENUECAT_OFFERINGS.ACCOUNT_STORAGE,
    packageId: 'storage_starter_monthly',
    productId: 'gathrd_storage_starter_monthly_299',
    displayName: 'Storage Starter',
    fallbackPriceLabel: '$2.99/month',
    description: 'Account storage for up to 5 GB.',
    storageRangeLabel: '0–5 GB',
    sortOrder: 10,
  },
  {
    key: 'storage_plus',
    kind: 'account_storage',
    offeringId: WEB_REVENUECAT_OFFERINGS.ACCOUNT_STORAGE,
    packageId: 'storage_plus_monthly',
    productId: 'gathrd_storage_plus_monthly_699',
    displayName: 'Storage Plus',
    fallbackPriceLabel: '$6.99/month',
    description: 'Account storage from 5–25 GB.',
    storageRangeLabel: '5–25 GB',
    sortOrder: 20,
  },
  {
    key: 'storage_pro',
    kind: 'account_storage',
    offeringId: WEB_REVENUECAT_OFFERINGS.ACCOUNT_STORAGE,
    packageId: 'storage_pro_monthly',
    productId: 'gathrd_storage_pro_monthly_999',
    displayName: 'Storage Pro',
    fallbackPriceLabel: '$9.99/month',
    description: 'Account storage from 25–100 GB.',
    storageRangeLabel: '25–100 GB',
    sortOrder: 30,
  },
  {
    key: 'storage_max',
    kind: 'account_storage',
    offeringId: WEB_REVENUECAT_OFFERINGS.ACCOUNT_STORAGE,
    packageId: 'storage_max_monthly',
    productId: 'gathrd_storage_max_monthly_1499',
    displayName: 'Storage Max',
    fallbackPriceLabel: '$14.99/month',
    description: 'Account storage for 100 GB and above.',
    storageRangeLabel: '100+ GB',
    sortOrder: 40,
  },
];

export const WEB_PURCHASE_CATALOG = [
  ...WEB_EVENT_PACKAGES,
  ...WEB_EVENT_UPGRADES,
  ...WEB_HOST_VIDEO_ADDONS,
  ...WEB_GUEST_VIDEO_PASSES,
  ...WEB_ACCOUNT_STORAGE,
];
